import { useCallback, useEffect } from 'react'
import { useEnterGiveaway, useMe } from '@/hooks/queries'
import { clearReferrer, loadReferrer } from '@/lib/referral'
import { useAuthStore } from '@/store/auth'
import { useOverlayStore } from '@/store/overlays'
import type { Giveaway } from '@/models/giveaway'

/**
 * Entering a giveaway, including the signed-out path.
 *
 * A visitor who clicks an entry option is told to sign in — and then, in the
 * Quasar app and in most implementations of this, has to find the button again
 * afterwards. The intent is held instead, and completed as soon as the session
 * exists, so signing in finishes the thing they were doing.
 */
export function useGiveawayEntry(giveaway: Giveaway | undefined) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const openOverlay = useOverlayStore((state) => state.open)
  const pendingEntry = useOverlayStore((state) => state.pendingEntry)
  const setPendingEntry = useOverlayStore((state) => state.setPendingEntry)
  const enter = useEnterGiveaway()
  const { data: me } = useMe()
  const myId = me?.id ?? null

  const giveawayId = giveaway?.id ?? null

  /** Enters, crediting whoever referred them here; the credit only counts once. */
  const submit = useCallback(
    (id: number, objectiveId: number) => {
      enter.mutate(
        {
          giveaway_id: id,
          giveaway_objective_id: objectiveId,
          referrer_id: loadReferrer(id, myId),
        },
        { onSuccess: () => clearReferrer(id) },
      )
    },
    [enter, myId],
  )

  const requestEntry = useCallback(
    (objectiveId: number | null) => {
      if (objectiveId === null || giveawayId === null) return
      const objective = giveaway?.objectiveById(objectiveId)
      // Referral entries come from other people entering, not from a click.
      if (objective?.is_entered || objective?.is_referral) return
      if (giveaway?.has_ended) return

      if (!isAuthenticated) {
        setPendingEntry({ giveawayId, objectiveId })
        openOverlay('auth')
        return
      }

      submit(giveawayId, objectiveId)
    },
    [giveaway, giveawayId, isAuthenticated, openOverlay, setPendingEntry, submit],
  )

  // Finish what they started, once the session is real.
  useEffect(() => {
    if (!isAuthenticated || !pendingEntry || giveawayId === null) return
    if (pendingEntry.giveawayId !== giveawayId) return

    setPendingEntry(null)
    if (giveaway?.objectiveById(pendingEntry.objectiveId)?.is_entered) return
    submit(pendingEntry.giveawayId, pendingEntry.objectiveId)
  }, [isAuthenticated, pendingEntry, giveawayId, giveaway, setPendingEntry, submit])

  return {
    requestEntry,
    isEntering: enter.isPending,
    /** Which objective is mid-flight, so only that row shows a spinner. */
    enteringObjectiveId: enter.isPending ? (enter.variables?.giveaway_objective_id ?? null) : null,
  } as const
}
